import Check from '@mui/icons-material/Check';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { getV1CompaniesCompanyIdDepartments } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { SelectedFilter } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { Department } from '~hooks/useDepartment/models';
import useOrdersDispatchFilters from '~pages/Dispatch/hooks/useOrdersDispatchFilters';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { MEDIUM_CHIP_HEIGHT_IN_PX } from '~theme/AppTheme';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import QuickFilterChip from './QuickFilterChip';

const MAX_VISIBLE_DEPARTMENTS = 6;

type DepartmentOption = { id: string; name: string };

interface DepartmentsFilterProps {
  sx?: SxProps<Theme>;
}

const DepartmentsFilter = observer(({ sx }: DepartmentsFilterProps) => {
  const theme = useTheme();
  const quickDepartmentFiltersFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.quickDepartmentFilters,
  });

  const { ordersDispatchStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [fittingChipsCount, setFittingChipsCount] = useState(MAX_VISIBLE_DEPARTMENTS);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const isChipsRowRendered =
    quickDepartmentFiltersFeatureFlagEnabled && departments.length > 1;

  const [chipDepartments, hiddenDepartments] = useMemo(() => {
    return [
      departments.slice(0, MAX_VISIBLE_DEPARTMENTS),
      departments.slice(fittingChipsCount),
    ];
  }, [departments, fittingChipsCount]);

  const selectedDepartmentIds = ordersDispatchStore.filters.departmentIds ?? [];

  const handleDepartmentChipClick = useCallback(
    (departmentId: string) => {
      const newIds = [...selectedDepartmentIds];
      const index = selectedDepartmentIds.findIndex((id) => id === departmentId);

      if (index > -1) {
        newIds.splice(index, 1);
      } else {
        newIds.push(departmentId);
      }

      ordersDispatchStore.setFilters({ departmentIds: newIds }, true);
    },
    [JSON.stringify(selectedDepartmentIds)],
  );

  const { updateAvailableOptions, updateAppliedFilterChipsCollectedData } =
    useOrdersDispatchFilters();

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await getV1CompaniesCompanyIdDepartments({
        path: { 'company-id': companyId },
        query: { 'page[limit]': MAX_API_PAGE_SIZE },
      });

      const departments = response.data.data.map((department) => {
        const parsed = Department.parse(department);

        return { id: parsed.id, name: parsed.name };
      });
      const departmentsWithNoneOption = departments.concat([
        {
          id: 'none',
          name: t('dispatch.dispatch_v2.filters.departments_filter_none_option'),
        },
      ]);
      const departmentsWithNoneOptionAsFilterMenuOptions = departmentsWithNoneOption.map(
        (department) => {
          return { label: department.name, value: department.id };
        },
      );

      updateAvailableOptions(
        SelectedFilter.DEPARTMENTS,
        departmentsWithNoneOptionAsFilterMenuOptions,
      );
      updateAppliedFilterChipsCollectedData(
        SelectedFilter.DEPARTMENTS,
        departmentsWithNoneOptionAsFilterMenuOptions,
      );

      setDepartments(departmentsWithNoneOption);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.departments.failed_to_fetch'),
      );
    }
  }, [companyId]);

  useEffect(() => {
    if (quickDepartmentFiltersFeatureFlagEnabled) {
      fetchDepartments();
    }
  }, [quickDepartmentFiltersFeatureFlagEnabled, fetchDepartments]);

  // Chips that don't fit stay rendered, only hidden: removing them would shrink the
  // container to the chips left, so they could never come back.
  useLayoutEffect(() => {
    const chipsContainer = chipsContainerRef.current;

    if (!chipsContainer) {
      return;
    }

    const updateFittingChipsCount = () => {
      const chipTops = Array.from(
        chipsContainer.children,
        (chip) => chip.getBoundingClientRect().top,
      );
      const firstWrappedChipIndex = chipTops.findIndex((top) => top > chipTops[0]);

      setFittingChipsCount(
        firstWrappedChipIndex === -1 ? chipTops.length : firstWrappedChipIndex,
      );
    };

    updateFittingChipsCount();

    const resizeObserver = new ResizeObserver(updateFittingChipsCount);
    resizeObserver.observe(chipsContainer);
    Array.from(chipsContainer.children).forEach((chip) => resizeObserver.observe(chip));

    return () => resizeObserver.disconnect();
  }, [departments, isChipsRowRendered]);

  if (!isChipsRowRendered) {
    return null;
  }

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          ref={chipsContainerRef}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            height: MEDIUM_CHIP_HEIGHT_IN_PX,
            overflow: 'hidden',
          }}
        >
          {chipDepartments.map((department, index) => (
            <DepartmentChip
              key={department.id}
              department={department}
              isHidden={index >= fittingChipsCount}
              isSelected={selectedDepartmentIds.includes(department.id)}
              onClick={() => handleDepartmentChipClick(department.id)}
            />
          ))}
        </Box>

        {hiddenDepartments.length > 0 && (
          <Menu
            sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
            menuTrigger={
              <SmallButton
                size="small"
                startIcon={<MoreHoriz />}
                sx={{
                  [`&.${buttonClasses.root}`]: {
                    backgroundColor: theme.semantic.surfaceRaised,
                    color: theme.palette.text.primary,
                    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    borderRadius: theme.brandV2.borderRadius,
                  },
                }}
              >
                {t('common.more')}
              </SmallButton>
            }
          >
            {hiddenDepartments.map((department) => (
              <MenuItem
                key={department.id}
                onClick={() => handleDepartmentChipClick(department.id)}
                data-ui="page-dispatch__departments-filter__button"
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                    {department.name}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {selectedDepartmentIds.includes(department.id) && (
                      <Check
                        sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                      />
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Box>
    </Box>
  );
});

interface DepartmentChipProps {
  department: DepartmentOption;
  isHidden: boolean;
  isSelected?: boolean;
  onClick: () => void;
}

function DepartmentChip({
  department,
  isHidden,
  isSelected,
  onClick,
}: DepartmentChipProps) {
  return (
    <QuickFilterChip
      label={department.name}
      onClick={onClick}
      isHidden={isHidden}
      isSelected={Boolean(isSelected)}
      maxWidth="120px"
      data-ui="page-dispatch__departments-filter__button"
    />
  );
}

export default DepartmentsFilter;
